import { EMPTY, Observable, catchError, forkJoin, map, mergeMap, of, switchMap, take, tap, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store, select } from '@ngrx/store';
import { GRAPH_URL, GRAPH_URL_MICROSOFT_BETA, TENANT_ID } from '../graph-api.config';
import { getADUser } from '../selectors/auth.selectors';
import { flatten } from 'lodash';
import * as i0 from "@angular/core";
import * as i1 from "@ngrx/store";
import * as i2 from "@angular/common/http";
const UUID_REGEX = new RegExp('[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}');
let cachedUsers = {};
export class GraphApi {
    constructor(store, http) {
        this.store = store;
        this.http = http;
    }
    searchUsersBeta(searchString) {
        const params = {
            $filter: ''
        };
        if (searchString && searchString.length > 0) {
            // Search by First name
            searchString = searchString.replace("'", "''");
            const names = searchString.split(' ');
            params.$filter += `(startswith(givenName,'${names[0]}')`;
            // Search by e-mail
            if (names.length === 1) {
                params.$filter += ` or startswith(mail,'${names}')`;
            }
            // // Search by last name
            if (names.length === 1 || names[1] === '') {
                params.$filter += ` or startswith(surname,'${names[0]}'))`;
            }
            else {
                params.$filter += ` and startswith(surname,'${names[1]}'))`;
            }
        }
        return this.retrieveUsersBeta(params);
    }
    retrieveUsersBeta(params, nextLink = null) {
        const url = nextLink ? nextLink : GRAPH_URL_MICROSOFT_BETA + '/users';
        return this.http
            .get(url, { params: nextLink ? {} : params })
            .pipe(switchMap(res => {
            if (res['@odata.nextLink']) {
                return this.retrieveUsersBeta(params, res['@odata.nextLink']).pipe(map(nextRes => res.value.concat(nextRes)));
            }
            else {
                return of(res.value);
            }
        }), catchError(error => {
            this.throwErrorMessage(error, 'Error searching users');
            return [];
        }));
    }
    /**
     * Does a query to the graph api for, it doesn't support $search, so we search by displayName and given name.
     * displayName starts with lastName.
     *
     * @param [searchString]
     * @returns
     */
    searchUsers(searchString, options) {
        const params = {
            $filter: 'accountEnabled eq true'
        };
        if (searchString && searchString.length > 0) {
            // Search by First name
            searchString = searchString.replace("'", "''");
            const names = searchString.split(' ');
            params.$filter += ` and (startswith(givenName,'${names[0]}')`;
            // Search by e-mail
            if (names.length === 1) {
                params.$filter += ` or startswith(mail,'${names}')`;
                if (options && options.includeLdap) {
                    params.$filter += ` or startswith(mailNickname,'${names}')`;
                }
            }
            // Search by last name
            if (names.length === 1 || names[1] === '') {
                params.$filter += ` or startswith(surname,'${names[0]}'))`;
            }
            else {
                params.$filter += ` and startswith(surname,'${names[1]}'))`;
            }
        }
        return this.http
            .get(GRAPH_URL + TENANT_ID + '/users', {
            params: params
        })
            .pipe(map(res => res.value), catchError(error => {
            this.throwErrorMessage(error, 'Error searching users');
            return [];
        }));
    }
    /**
     * Retrieves all the user data from the passed in user id.
     * The function also caches users, to minimize the amount of graph requests.
     *
     * @param ids user object id
     * @returns UserProfile list
     */
    getUsers(ids) {
        ids = ids.filter(id => UUID_REGEX.test(id));
        if (ids.length === 0) {
            throwError('Invalid user ID');
            return EMPTY;
        }
        const nonCachedUserIds = ids.filter(id => !this.getCacheUsers(id));
        if (nonCachedUserIds.length > 0) {
            return this.retrieveUsers(nonCachedUserIds).pipe(tap(users => this.cacheUsers(users)), map(_users => ids.map(id => this.getCacheUsers(id))));
        }
        return of(ids.map(id => this.getCacheUsers(id)));
    }
    /**
     * Retrieves all the user data from the passed in user ldap.
     * which is the mailNickname in the graph url.
     *
     * @deprecated This method was replaced by a method with same name on the MS Graph API (newer version).
     *
     * @param ldaps user ldap list
     * @returns UserProfile list
     */
    getUsersByLdap(ldaps) {
        if (ldaps.length === 0) {
            throwError('Invalid user ID');
            return EMPTY;
        }
        let i, j;
        const chunk = 10;
        const requestChunks = [];
        for (i = 0, j = ldaps.length; i < j; i += chunk) {
            const temparray = ldaps.slice(i, i + chunk);
            requestChunks.push(this.getUsersByLdapInChunks(temparray));
        }
        return forkJoin(requestChunks).pipe(map(response => flatten(response)));
    }
    /**
     * Retrieves all the user data from the passed in user ldaps.
     * This array should have a max of 10 items, Graph api doesn't allow for more conditions at one time.
     * which is the mailNickname in the graph url.
     *
     * @param ldaps user ldap list
     * @returns UserProfile list
     */
    getUsersByLdapInChunks(ldaps) {
        const query = `(mailNickname eq '${ldaps.join("' or mailNickname eq '")}')`;
        return this.http
            .get(GRAPH_URL + TENANT_ID + '/users', {
            params: { $filter: query }
        })
            .pipe(map(res => res.value), catchError(error => {
            this.throwErrorMessage(error, 'Error searching users');
            return [];
        }), take(1));
    }
    /**
     * Gets the full user object including the manager info
     *
     * @param id can be email or objectId
     * @returns
     */
    getFullUserDetails(id) {
        return forkJoin([
            this.getUserByEmail(id),
            this.getUserManager(id),
            this.getUserThumbnail(id)
        ]).pipe(map(([userDetails, userManager, thumbnailUrl]) => ({
            ...userDetails,
            manager: userManager,
            thumbnailPhotoUrl: thumbnailUrl
        })), catchError(error => {
            this.throwErrorMessage(error, 'Error retrieving user profile');
            return [];
        }));
    }
    getUserThumbnail(id) {
        const options = { responseType: 'blob' };
        return this.http.get(`${GRAPH_URL + TENANT_ID}/users/${id}/thumbnailPhoto`, options).pipe(switchMap(resp => new Observable(observer => {
            const reader = new FileReader();
            reader.onload = () => {
                observer.next(reader.result);
                observer.complete();
            };
            reader.readAsDataURL(resp);
        })), catchError(() => of('')));
    }
    getUserByEmail(email) {
        return this.http.get(GRAPH_URL + TENANT_ID + '/users/' + email).pipe(catchError(error => {
            this.throwErrorMessage(error, 'Error retrieving user profile');
            return [];
        }));
    }
    /**
     * Gets the manager of the user
     *
     * @param id can be the objectId or user email
     * @returns
     */
    getUserManager(id) {
        return this.http
            .get(GRAPH_URL + TENANT_ID + '/users/' + id + '/manager')
            .pipe(catchError(() => of(null)));
    }
    /**
     * Uses the getUsers function to retrieve a single user profile
     *
     * @param id user object id
     * @returns UserProfile
     */
    getUser(id) {
        return this.getUsers([id]).pipe(take(1), map(res => res[0]));
    }
    /**
     * Retrieves the user profile for the current user
     *
     * @returns UserProfile
     */
    getCurrentUser() {
        return this.store.pipe(select(getADUser), mergeMap(user => this.getUser(user.profile.oid)));
    }
    clearUserCache() {
        cachedUsers = {};
    }
    getCacheUsers(userId) {
        return cachedUsers[userId];
    }
    /**
     * Caches users in the cachedUsers object by the user objectId
     *
     * @param users UserProfile list
     */
    cacheUsers(users) {
        users.forEach(user => (cachedUsers[user.objectId] = user));
    }
    /**
     * Uses the 'getObjectsByObjectIds' endpoint on the azure graph to retrieve users by the user object ids.
     *
     * @param ids user object id
     * @returns UserProfile list
     */
    retrieveUsers(ids) {
        return this.http
            .post(GRAPH_URL + TENANT_ID + '/getObjectsByObjectIds', {
            objectIds: ids,
            types: ['user']
        })
            .pipe(map(res => res.value), catchError(error => {
            this.throwErrorMessage(error, 'Error retrieving user profiles');
            return [];
        }));
    }
    // tslint:disable-next-line:no-any
    throwErrorMessage(error, message) {
        const json = error?.json ? error.json() : JSON.stringify(error || 'Unknown');
        const errorMessage = message ? message + ' | ' + json : json;
        throwError(errorMessage);
    }
    static { this.ɵfac = function GraphApi_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || GraphApi)(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.HttpClient)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: GraphApi, factory: GraphApi.ɵfac }); }
}
//# sourceMappingURL=graph-api.service.js.map